import axios, { AxiosError, AxiosResponse } from "axios";

export class UserFriendlyError extends Error {
  userFriendly = true;

  constructor(message: string) {
    super(message);
  }
}

export function isUserFriendlyError(err: unknown): err is UserFriendlyError {
  return typeof err === "object" && err !== null && "userFriendly" in err;
}

/**
 * SKE-3462: Read the structured `api_error_code` the server attaches to
 * ApiError responses (see `utils/errors/sendError.js`). Used internally to
 * key the friendly-message map below. Not exported — call sites that need
 * to branch on the code directly should inline the same lookup; the body
 * is two lines and exporting it complicates module-level mocks across the
 * codebase that already wholesale-mock `getUserFriendlyErrorMessage`.
 */
function getApiErrorCode(err: AxiosError | Error | unknown): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const code = (err as any)?.response?.data?.api_error_code;
  return typeof code === "string" && code.length > 0 ? code : null;
}

/**
 * SKE-3462: Friendly messages keyed by the server's `api_error_code`. Add
 * entries here when new structured errors are introduced — keeps the toast
 * copy consistent across screens that surface the same backend signal.
 */
const API_ERROR_CODE_MESSAGES: Record<string, string> = {
  account_limit_reached:
    "You've reached your social-account limit. Increase your account quota in Manage Plan to add more.",
  user_not_permitted_to_add_accounts:
    "Your user is not permitted to add accounts. Please ask your administrator to grant access.",
  // SKE-3514: Chargebee enforces a 24-hour gap between consecutive plan
  // ramps. The Manage Plan SPA exposes a "Cancel scheduled change"
  // banner for self-service recovery (Option 4); this toast covers the
  // race window where someone clicks another plan before the banner
  // refresh lands, or the legacy plans-modal path that isn't wired to
  // the new banner UI.
  ramp_conflict_24h:
    "You've recently scheduled a plan change. Open Manage Plan to cancel the pending change before switching, or wait until tomorrow.",
};

export function getUserFriendlyErrorMessage(
  err: AxiosError | Error | unknown,
  defaultMessage = "Something went wrong."
): string {
  // SKE-3462: prefer the structured `api_error_code` over the message string.
  // Falls back to the historical extraction order if no code is present.
  const apiErrorCode = getApiErrorCode(err);
  if (apiErrorCode && API_ERROR_CODE_MESSAGES[apiErrorCode]) {
    return API_ERROR_CODE_MESSAGES[apiErrorCode];
  }

  const structuredResponseMessage = getStructuredResponseMessage(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err as any)?.response?.data
  );
  if (structuredResponseMessage) {
    return structuredResponseMessage;
  }

  if (isUserFriendlyError(err)) {
    return err.message;
  }

  // The migrated generated-server client throws a GeneratedSchedugramServerApiError,
  // which is NOT an AxiosError, so the axios branch below never runs for it and its
  // server message would otherwise collapse to the generic default. Read a top-level
  // `error` string off the response body here so `{ error: "…" }` responses surface
  // for both axios and the generated client. Runs after the isUserFriendlyError guard
  // so explicit user-friendly messages still win, and before the axios branch (which
  // makes its own `response.data.error` string check redundant). (SKE-4075)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const topLevelError = (err as any)?.response?.data?.error;
  if (typeof topLevelError === "string" && topLevelError.trim()) {
    return topLevelError;
  }

  if (axios.isAxiosError(err)) {
    return getErrorMessageFromAxiosError(err, defaultMessage);
  }

  return defaultMessage;
}

function getErrorMessageFromAxiosError(
  err: AxiosError,
  defaultMessage: string
): string {
  if (err.response && isJsonResponse(err.response)) {
    return getErrorMessageFromAxiosResponse(err.response, defaultMessage);
  } else if (err.response && err.response.status === 401) {
    return "There was a permissions error with that request. Please try logging out and back in before trying again, or speak to support if it persists.";
  } else {
    return defaultMessage;
  }
}

function getErrorMessageFromAxiosResponse(
  response: AxiosResponse,
  defaultMessage: string
): string {
  const structuredResponseMessage = getStructuredResponseMessage(response.data);

  // A top-level string `response.data.error` is handled earlier in
  // getUserFriendlyErrorMessage (topLevelError), so it is intentionally not
  // re-checked here. (SKE-4075)
  if (typeof response.data === "string") {
    return response.data;
  } else if (structuredResponseMessage) {
    return structuredResponseMessage;
  } else {
    return defaultMessage;
  }
}

function getStructuredResponseMessage(data: unknown): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const responseData = data as any;
  const message = responseData?.message;
  const nestedMessages = responseData?.messages;
  const nestedErrorMessage = responseData?.error?.message;
  const nestedMessage = getFirstNestedMessage(nestedMessages);

  if (typeof message === "string") {
    if (isGenericEnvelopeMessage(message) && nestedMessage) {
      return nestedMessage;
    }

    return message;
  }

  if (
    typeof message === "object" &&
    typeof Object.values(message)[0] === "string"
  ) {
    return Object.values(message)[0] as string;
  }

  if (typeof nestedErrorMessage === "string") {
    return nestedErrorMessage;
  }

  if (nestedMessage) {
    return nestedMessage;
  }

  return undefined;
}

function getFirstNestedMessage(messages: unknown): string | undefined {
  if (!Array.isArray(messages) || messages.length <= 0) {
    return undefined;
  }

  const [firstMessage] = messages;

  if (typeof firstMessage === "string" && firstMessage.trim()) {
    return firstMessage.trim();
  }

  if (
    firstMessage &&
    typeof firstMessage === "object" &&
    "message" in firstMessage &&
    typeof firstMessage.message === "string" &&
    firstMessage.message.trim()
  ) {
    return firstMessage.message.trim();
  }

  return undefined;
}

function isGenericEnvelopeMessage(message: string) {
  return ["Unable to send message"].includes(message.trim());
}

function isJsonResponse(response: AxiosResponse): boolean {
  const contentType = response.headers["content-type"];
  return typeof contentType === "string"
    ? contentType.includes("application/json")
    : false;
}
